import React, { useState, useEffect, useRef } from 'react';
import { User, Briefcase, Award, Code2, Phone, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Portfolio() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [colorBlobs, setColorBlobs] = useState([]);
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const roles = [
    "Software Engineer",
    "AI Software Engineer",
    "Full Stack Developer"
  ];

  const createColorBlob = (x, y) => {
    const newBlob = {
      id: Date.now() + Math.random(),
      x: x,
      y: y,
      color: `hsl(${Math.random() * 360}, 70%, 60%)`,
      size: Math.random() * 100 + 50,
    };
    setColorBlobs(prev => [...prev.slice(-10), newBlob]);
  };

  const getRelativeCoordinates = (clientX, clientY) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    }
    return { x: 0, y: 0 };
  };

  const handleDownloadResume = () => {
    // Create a link element and trigger download
    const link = document.createElement('a');
    link.href = '/resume/Siddharth_Singh_Khati_Resume.pdf'; // Path to your PDF file
    link.download = 'Siddharth_Singh_Khati_Resume.pdf'; // Name for the downloaded file
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNavigation = (section) => {
    navigate(`/${section}`);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isTouching) {
        const { x, y } = getRelativeCoordinates(e.clientX, e.clientY);
        setMousePosition({ x, y });
        createColorBlob(x, y);
      }
    };

    const handleTouchStart = (e) => {
      setIsTouching(true);
      const touch = e.touches[0];
      const { x, y } = getRelativeCoordinates(touch.clientX, touch.clientY);
      setMousePosition({ x, y });
      createColorBlob(x, y);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const { x, y } = getRelativeCoordinates(touch.clientX, touch.clientY);
      setMousePosition({ x, y });
      createColorBlob(x, y);
    };

    const handleTouchEnd = (e) => {
      setIsTouching(false);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isTouching]);

  useEffect(() => {
    const timer = setInterval(() => {
      setColorBlobs(prev => prev.filter(blob => Date.now() - blob.id < 2000));
    }, 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const roleTimer = setInterval(() => {
      setCurrentRoleIndex(prev => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(roleTimer);
  }, [roles.length]);

  return (
    <div
      ref={containerRef}
      className={`min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 overflow-hidden fixed inset-0 ${!isTouching ? 'cursor-none' : ''}`}
    >
      {colorBlobs.map(blob => (
        <div
          key={blob.id}
          className="absolute pointer-events-none animate-ping opacity-20"
          style={{
            left: blob.x - blob.size / 2,
            top: blob.y - blob.size / 2,
            width: blob.size,
            height: blob.size,
            background: `radial-gradient(circle, ${blob.color} 0%, transparent 70%)`,
            borderRadius: '50%',
            animation: 'fade-out 2s ease-out forwards',
          }}
        />
      ))}

      <div
        className={`fixed w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full pointer-events-none z-50 mix-blend-difference transition-opacity duration-200 ${isTouching ? 'opacity-0' : 'opacity-100'}`}
        style={{
          left: mousePosition.x - 8,
          top: mousePosition.y - 8,
          transition: 'all 0.1s ease-out',
          touchAction: 'none'
        }}
      />

      {isTouching && (
        <div
          className="fixed w-8 h-8 border-2 border-purple-500 rounded-full pointer-events-none z-50 animate-pulse"
          style={{
            left: mousePosition.x - 16,
            top: mousePosition.y - 16,
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, transparent 70%)',
            touchAction: 'none'
          }}
        />
      )}

      <header className="absolute top-0 left-0 right-0 z-40 p-4 sm:p-6 lg:p-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-gray-700 text-xs sm:text-sm lg:text-base font-medium">Open to new collaborations!</span>
          </div>
          
          {/* Download Resume Button in Header */}
          <button
            onClick={handleDownloadResume}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white 
              px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 
              rounded-full shadow-lg hover:shadow-xl 
              transition-all duration-300 transform hover:scale-105 
              focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" strokeWidth={2} />
            <span className="text-xs sm:text-sm md:text-base font-medium">Resume</span>
          </button>
        </div>
      </header>

      <div className="flex items-center justify-center min-h-screen px-[10%] py-4 sm:py-6 lg:py-8">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="text-center lg:text-left space-y-4 sm:space-y-6 lg:space-y-8 order-2 lg:order-1">
              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-gray-700 font-light">
                  Hey, I'm Siddharth Singh Khati 👋
                </h1>
                <div className="relative h-16 sm:h-20 lg:h-24 xl:h-28 2xl:h-32 flex items-center justify-center lg:justify-start">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold leading-tight role-transition">
                    <span className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 bg-clip-text text-transparent">
                      {roles[currentRoleIndex]}
                    </span>
                  </h2>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 text-gray-600 max-w-lg mx-auto lg:mx-0">
                <div className="relative">
                  <p className="text-base sm:text-lg lg:text-xl font-medium bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent leading-relaxed">
                    Crafting extraordinary digital experiences through innovative technology solutions
                  </p>
                  <div className="absolute -left-1 top-0 w-1 h-full bg-gradient-to-b from-purple-500 to-blue-500 rounded-full opacity-60"></div>
                </div>
                <p className="text-sm sm:text-base lg:text-lg text-gray-600 font-light leading-relaxed">
                  Specializing in cutting-edge AI development, seamless full-stack architectures, and transformative machine learning solutions that redefine what's possible in the digital realm.
                </p>
              </div>

              <div className="flex justify-center lg:justify-start space-x-6 sm:space-x-8 lg:space-x-10 pt-6 sm:pt-8">
                <button
                  onClick={() => handleNavigation('')}
                  className="flex flex-col items-center space-y-2 sm:space-y-3 group cursor-pointer focus:outline-none"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 group-hover:-translate-y-1">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-800" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">Me</span>
                </button>

                <button
                  onClick={() => handleNavigation('projects')}
                  className="flex flex-col items-center space-y-2 sm:space-y-3 group cursor-pointer focus:outline-none"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 group-hover:-translate-y-1">
                    <Code2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-800" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">Projects</span>
                </button>

                <button
                  onClick={() => handleNavigation('skills')}
                  className="flex flex-col items-center space-y-2 sm:space-y-3 group cursor-pointer focus:outline-none"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 group-hover:-translate-y-1">
                    <Award className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-800" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">Skills</span>
                </button>

                <button
                  onClick={() => handleNavigation('experience')}
                  className="flex flex-col items-center space-y-2 sm:space-y-3 group cursor-pointer focus:outline-none"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 group-hover:-translate-y-1">
                    <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-800" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">Experience</span>
                </button>

                <button
                  onClick={() => handleNavigation('contact')}
                  className="flex flex-col items-center space-y-2 sm:space-y-3 group cursor-pointer focus:outline-none"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 group-hover:-translate-y-1">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-800" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">Contact</span>
                </button>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 xl:w-96 xl:h-96 bg-gradient-to-r from-purple-400 via-pink-400 to-green-400 rounded-full animate-spin-slow opacity-30 blur-xl"></div>

                <div className="relative w-40 h-40 sm:w-52 sm:h-52 lg:w-64 lg:h-64 xl:w-80 xl:h-80 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center shadow-2xl">
                  <img
                    src="images/Siddharth_Singh_Khati.png"
                    alt="Siddharth's Avatar"
                    className="w-32 h-32 sm:w-44 sm:h-44 lg:w-56 lg:h-56 xl:w-72 xl:h-72 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-out {
          0% { opacity: 0.3; transform: scale(0); }
          50% { opacity: 0.2; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.2); }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .role-transition {
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .bg-size-200 {
          background-size: 200% 200%;
        }

        .bg-pos-0 {
          background-position: 0% 50%;
        }

        .bg-pos-100 {
          background-position: 100% 50%;
        }
      `}</style>
    </div>
  );
}